import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PermissionCheck } from "@/components/CheckPermission";
import { Permissions } from "@/components/CheckPermission/enum";
import { AddButton } from "@/components/button/add-button";
import { useTheme } from "@/contexts/theme";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { PlusIcon } from "../icons/header-section-icons";
import { useStyle } from "../../style";

interface ICreateNewButtonProps {
  onClick: () => void;
  /** Keeps the button from stretching when it sits next to the page title. */
  compact?: boolean;
}

/**
 * "Create new" action for the documents list, shared by the statistics row (desktop)
 * and the page title row (mobile). Uses the bordered design of the other document headers.
 */
const CreateNewButton = ({ onClick, compact }: ICreateNewButtonProps) => {
  const { t } = useTranslation();
  const { classes } = useStyle();
  const { primaryColor } = useGomakeTheme();
  const { currentTheme } = useTheme();
  const isHpClient = currentTheme === "hp-client";
  const label = t("sales.quote.createNew");
  const isRTL = t("direction") === "rtl";

  if (isHpClient) {
    return (
      <PermissionCheck userPermission={Permissions.CREATE_QUOTES}>
        <AddButton
          label={label}
          onClick={onClick}
          style={
            compact ? { minWidth: "auto", whiteSpace: "nowrap" } : undefined
          }
        />
      </PermissionCheck>
    );
  }

  return (
    <PermissionCheck userPermission={Permissions.CREATE_QUOTES}>
      <Button
        className={currentTheme === "modern" && !compact ? "gm-ql-create" : undefined}
        sx={{
          ...classes.createNewDocument,
          borderRadius: compact ? "8px" : "4px",
          flexShrink: 0,
          whiteSpace: "nowrap",
          "& .MuiButton-startIcon": {
            ...(isRTL
              ? { marginLeft: "4px", marginRight: 0 }
              : { marginRight: "4px", marginLeft: 0 }),
          },
        }}
        onClick={onClick}
        startIcon={<PlusIcon stroke={primaryColor(500)} />}
      >
        {label}
      </Button>
    </PermissionCheck>
  );
};

export { CreateNewButton };
